import { ContentClassEnum } from 'content-service-client';

import * as FolderAPI from './FolderAPI';

export type FolderAPIModule = typeof FolderAPI;

export type NavCategory = {
    url: string;
    title: string;
    telemetry?: string;
    isNew?: boolean;
};

export type Subscription = {
    id: number;
    uniqueId: string;
    planId: number;
    organizationId: number;
    siteId: number;
    fullName: string;
    status: string;
    subscriptionEndDate: object;
    daysLeftOnSubscription: number;
    dateAdded: string;
    dateUpdated: string;
    isStarterBundle: boolean;
    isMakerOnly: boolean;
    isUnlimitedBundle: boolean;
    isGrowthBundle: boolean;
    isPremierePro: boolean;
    planClassification: PlanClassification;
    rolloverPlanId?: number;
    monthlyDownloadsRemaining?: number;
    isLegacy: boolean;
    recurlySubscriptionId: string;
    recurlySubscriptionUuid: string;
};

export enum PlanClassification {
    Legacy = 'Legacy',
    Basic = 'Basic',
    Starter = 'Starter',
    Bundle = 'Bundle',
    Unlimited = 'Unlimited',
    Enterprise = 'Enterprise',
    Growth = 'Growth',
    Pro = 'Pro',
}

export type User = {
    id: number;
    email: string;
    displayName: string;
};

export type TopLevelCategories = {
    [Key in string]: TopLevelCategory;
};

export type TopLevelCategory = {
    url?: string;
    title?: string;
    items?: NavCategory[];
    newFeature?: boolean;
    categories?: {
        [categoryName: string]: CategoryWithSubs;
    };
    collections?: ContentCollection[];
};

export type ContentCollection = {
    active?: boolean;
    dateAdded?: string;
    dateUpdated?: string;
    description?: string;
    id?: number;
    name?: string;
    searchIndexable?: boolean;
    showInApi?: boolean;
    contentClasses?: ContentClassEnum[];
    view: ContentCollectionView;
};
type ContentCollectionView = {
    routeUri?: string;
    redirectUri?: string;
    heroBgImgUrl?: string;
    heroTitle?: string;
    heroSubtitle?: string;
    description?: string;
    metaTitle?: string;
    metaDescription?: string;
    metaKeywords?: string;
    leftXsellTitle?: string;
    leftXsellLinkTitle?: string;
    leftXsellLinkUrl?: string;
    leftXsellBgImgUrl?: string;
    rightXsellTitle?: string;
    rightXsellLinkTitle?: string;
    rightXsellLinkUrl?: string;
    rightXsellBgImgUrl?: string;
    allowSeoIndex?: boolean;
    showInDirectory?: boolean;
    showInMainNavDropdown?: boolean;
    directoryBackgroundImageUrl?: string;
};

export type CategoryWithSubs = {
    url: string;
    subCategories: NavCategory[];
    title?: string;
    newFeature?: boolean;
    isBeta?: boolean;
};

interface MenuItem {
    url: string;
    target?: string;
    title: string;
}

interface MenuList {
    [key: string]: MenuItem;
}

interface PlanCta {
    id: string;
    url: string;
    linkText: string;
    mobileLinkText: string;
}

export interface UserData {
    // ReactState::buildAuthState
    auth: {
        isLoggedIn: boolean;
        isIpAuth: boolean;
        isMobile: boolean;
        user: User;
        subscription: Subscription | undefined;
        primarySubscription: Subscription | null;
        videoSubscription: Subscription | null;
        audioSubscription: Subscription | null;
        imageSubscription: Subscription | null;
        permissions: { [key: string]: boolean };
        featureFlags: { [key: string]: boolean };
        hasAnySubscription: boolean;
        isEnterprise: boolean;
    };
    // NavBarViewModel::getUserData
    ui: {
        nav: {
            menu: {
                top_level_categories: TopLevelCategories;
                collections: Record<ContentClassEnum, NavCategory[]>;
                account: MenuList | undefined;
                showFavorites: boolean;
                isOpen: boolean;
                planCta: PlanCta | undefined;
                organizationName: string;
                shouldShowEnterpriseExperience: boolean;
                remainingExports: number;
                starterPlanRemainingDownloads: number;
                canManageTeam: boolean;
            };
        };
    };
}

export type RightSideMenuState = {
    user: User | undefined;
    subscription?: Subscription;
    permissions?: { [key: string]: boolean };
    isLoggedIn: boolean;
    isIpAuth: boolean;
    storyboardEnabled: boolean;
    account: MenuList | undefined;
    ui?: {
        nav: {
            menu: {
                account: unknown;
            };
        };
    };
    organizationName: string | undefined;
    planCta: PlanCta | undefined;
    remainingExports: number | undefined;
    shouldShowEnterpriseExperience: boolean;
    showFavorites: boolean;
    starterPlanRemainingDownloads: number | undefined;
    shouldUseSnapshotsUI: boolean;
    isUserOnGrowthPlan: boolean;
    isUserOnStarterPlan: boolean;
    userInitials: string;
    canManageTeam: boolean;
};

export type StockblocksStockItem = Record<string, unknown> & {
    id: number;
    title: string;
    contentType: string;
    contentClass: ContentClassEnum;
    assetId: string;
    contentId: number;
    type: string;
};

export interface Folder {
    dateUpdated: string;
    description?: string | null;
    id: number;
    isOwnedByMe: boolean;
    isShared: boolean;
    isUpdating?: boolean;
    itemCount: number;
    name: string;
    ownerName?: string;
    thumbnailUrl: string | null;
    uniqueId: string;
    contentIds: number[];
    stockItemIds: number[];
    hasUserSubscription?: boolean;
}

export type GetAllFoldersResponse = {
    bins: Record<string, Folder>;
    hasEnterpriseSupport: boolean;
    isInSharedFoldersBeta: boolean;
    organizationName: string | null;
};

export type UpdateFolderResponse = {
    bins: Record<string, Folder>;
};

// ─── TopNavigation Config Response ───────────────────────────────────

export interface TopNavLink {
    label: string;
    href: string;
    icon?: string;
}

export interface TopNavCtaConfig {
    label: string;
    href: string;
}

export interface TopNavRichItem {
    label: string;
    href: string;
    icon?: string;
    description?: string;
}

export interface TopNavDropdownSection {
    title: string;
    items: TopNavRichItem[];
}

export type TopNavDesktopDropdown =
    | {
          type: 'two-column';
          links: TopNavLink[];
          cta?: TopNavCtaConfig;
          categories?: { title: string; items: TopNavLink[] };
      }
    | {
          type: 'single-column';
          links: TopNavLink[];
      }
    | {
          type: 'layered';
          sections: TopNavDropdownSection[];
      };

export interface TopNavDesktopNavItem {
    id: string;
    label: string;
    href?: string;
    hasGradientText?: boolean;
    gradientIcon?: string;
    dropdown?: TopNavDesktopDropdown;
}

export interface TopNavMobileDropdown {
    links: TopNavLink[];
    cta?: TopNavCtaConfig;
    categories?: { title: string; items: TopNavLink[] };
}

export interface TopNavMobileNavItem {
    id: string;
    label: string;
    href?: string;
    hasGradientText?: boolean;
    gradientIcon?: string;
    dropdown?: TopNavMobileDropdown;
    showDividerAbove?: boolean;
}

export type TopNavActionItem =
    | { type: 'link'; label: string; href: string }
    | { type: 'cta'; label: string; href: string }
    | {
          type: 'icon';
          icon: string;
          href: string;
          ariaLabel: string;
          label?: string;
          chevron?: boolean;
          dropdown?: { links: TopNavLink[] };
      };

export interface TopNavMobileBottomAction {
    label: string;
    href: string;
    variant: 'primary' | 'outline';
}

export interface TopNavConfigResponse {
    desktop: {
        navItems: TopNavDesktopNavItem[];
        actions: TopNavActionItem[];
    };
    tablet: {
        navItems: TopNavDesktopNavItem[];
        actions: TopNavActionItem[];
        hamburgerNavItems: TopNavMobileNavItem[];
        overlayActions: TopNavMobileBottomAction[];
    };
    mobile: {
        navItems: TopNavMobileNavItem[];
        navBarCta?: { label: string; href: string };
        overlayActions: TopNavMobileBottomAction[];
    };
}
